import { __rest } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import { addErrorNotification, configurationSettings, loadLastValidatedEmail, LoyaltyMemberIdentifier, signMemberIn } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { MemberAuthenticationGrantType } from '@vista-digital/ocapi-types/v1';
import { observer } from 'mobx-react';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useFormValidator } from '../../hooks/use-form-validator';
import { Form } from '../../subcomponents/form/form/form';
import { withComponentWrapper } from '../../utils/with-component-wrapper';
import { MemberSignInFormContextInstance } from './types/context';
import { defaultMemberSignInFormData } from './types/data';
import { defaultMemberSignInFormProps, memberSignInFormPropsValidator } from './types/props';
import { getNotificationDetailsForError } from './utils/get-notification-details-for-error';
import { getValidationConfig } from './utils/get-validation-config';
const b = bemClassBuilder('v-member-sign-in-form');
const MemberSignInFormBase = (props, ref) => {
    const { config, displayText, events, children, formId } = props;
    const { memberIdentifier } = configurationSettings.data.loyalty.authentication;
    const validationConfig = getValidationConfig(memberIdentifier, config.validation);
    const { validationResult, validator } = useFormValidator(validationConfig);
    const [formData, setFormData] = useState(defaultMemberSignInFormData);
    const [isSubmitting, setIsSubmitting] = useState(false);
    useEffect(() => {
        (async () => {
            if (memberIdentifier === LoyaltyMemberIdentifier.Email) {
                const email = await loadLastValidatedEmail();
                if (email)
                    setFormData(data => (Object.assign(Object.assign({}, data), { email })));
            }
        })();
    }, [memberIdentifier]);
    const context = useMemo(() => ({
        onChange: (fieldName, value) => setFormData(data => (Object.assign(Object.assign({}, data), { [fieldName]: value }))),
        formData,
        validationResult,
        validationConfig,
        validator,
        disabled: isSubmitting,
        loading: false
    }), [formData, validationResult, isSubmitting, validationConfig, validator]);
    const handleFailedSignIn = error => {
        var _a;
        addErrorNotification(getNotificationDetailsForError(displayText, error), error);
        (_a = events.onAfterFailedSubmit) === null || _a === void 0 ? void 0 : _a.call(events);
    };
    const signIn = async () => {
        const { captcha } = formData, request = __rest(formData, ["captcha"]);
        await signMemberIn(Object.assign(Object.assign({}, request), { grant_type: MemberAuthenticationGrantType.Password }), formData.captcha);
    };
    const handleFormSubmittingStateChanged = (submitting) => {
        var _a;
        setIsSubmitting(submitting);
        (_a = events.onFormSubmittingStateChanged) === null || _a === void 0 ? void 0 : _a.call(events, submitting);
    };
    return (_jsx(Form, Object.assign({}, props, { id: formId, className: b.block(), validator: () => validator.validateForm(formData), events: Object.assign(Object.assign({}, events), { onSubmit: signIn, onAfterFailedSubmit: handleFailedSignIn, onFormSubmittingStateChanged: handleFormSubmittingStateChanged }), setFormData: setFormData, ref: ref }, { children: _jsx(MemberSignInFormContextInstance.Provider, Object.assign({ value: context }, { children: children })) })));
};
const MemberSignInForm = observer(forwardRef(MemberSignInFormBase));
MemberSignInForm.displayName = 'MemberSignInForm';
/**
 * The **Member Sign In Form** component allows a customer to sign in to their loyalty account using either their **email**
 * and **password**, or their **username** and **password**.
 */
export const MemberSignInFormComponent = withComponentWrapper(MemberSignInForm, defaultMemberSignInFormProps, memberSignInFormPropsValidator);
